import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Percent,
  CheckCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import quoteOrderAnalyticsService from '../../../api/crm/analytics/quoteOrderAnalyticsService';
import { formatDate } from '../../../utils/stringUtils';
import { getQuotationStatusClasses, formatQuotationStatus } from '../../../utils/quotationStatusUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const BREAKDOWN_COLORS = {
  draft: '#6b7280',
  created: '#3b82f6',
  sent: '#8b5cf6',
  seen: '#06b6d4',
  accepted: '#10b981',
  order: '#f59e0b',
  ordered: '#059669',
  rejected: '#ef4444'
};

const BREAKDOWN_METRICS = [
  { key: 'draft', countKey: 'draftCount', amountKey: 'draftAmount', label: 'Quote Draft' },
  { key: 'created', countKey: 'createdCount', amountKey: 'createdAmount', label: 'Quote Created' },
  { key: 'sent', countKey: 'sentCount', amountKey: 'sentAmount', label: 'Quotation Sent' },
  { key: 'seen', countKey: 'seenCount', amountKey: 'seenAmount', label: 'Quotation Seen' },
  { key: 'accepted', countKey: 'acceptedCount', amountKey: 'acceptedAmount', label: 'Quotation Accepted' },
  { key: 'order', countKey: 'orderCount', amountKey: 'orderAmount', label: 'Ready to Order' },
  { key: 'ordered', countKey: 'orderedCount', amountKey: 'orderedAmount', label: 'Placed Order' },
  { key: 'rejected', countKey: 'rejectedCount', amountKey: 'rejectedAmount', label: 'Quotation Rejected' }
];

const BreakdownTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const formatCount = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

  const shortLabel = (label) =>
    label.replace('Quotation ', '').replace('Quote ', '');

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2.5 text-xs min-w-[220px]">
      <p className="font-semibold text-gray-900 mb-2 border-b border-gray-100 pb-1.5">{row.month}</p>
      <ul className="space-y-1.5">
        {BREAKDOWN_METRICS.map((metric) => (
          <li key={metric.key} className="flex items-start justify-between gap-3">
            <span className="font-medium shrink-0" style={{ color: BREAKDOWN_COLORS[metric.key] }}>
              {shortLabel(metric.label)}:
            </span>
            <span className="text-gray-800 text-right tabular-nums">
              <span className="font-semibold">{formatCount(row[metric.countKey])}</span>
              <span className="text-gray-400 mx-1">·</span>
              <span>₹{formatIndianAmount(row[metric.amountKey] || 0)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const QuoteOrderDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;
  const isExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const currentUserId = user?._id || user?.id;
  const currentUserName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me';
  const isManagerScope = isManager && currentUserId;

  const [dateRange, setDateRange] = useState('last_30_days');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [kpis, setKpis] = useState({
    totalQuotations: 0,
    quotesAccepted: 0,
    orders: 0,
    quoteToAcceptedRate: 0
  });
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [weeklyTrends, setWeeklyTrends] = useState([]);
  const [sixMonthTrend, setSixMonthTrend] = useState([]);
  const [sixMonthBreakdown, setSixMonthBreakdown] = useState([]);
  const [managers, setManagers] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [selectedManager, setSelectedManager] = useState(() => (isManagerScope ? currentUserId : 'all'));
  const [selectedExecutive, setSelectedExecutive] = useState('all');

  // Sorting state for weekly trends
  const [trendsSortColumn, setTrendsSortColumn] = useState('label');
  const [trendsSortDirection, setTrendsSortDirection] = useState('desc');

  // Sorting state for recent quotes
  const [quotesSortColumn, setQuotesSortColumn] = useState('createdAt');
  const [quotesSortDirection, setQuotesSortDirection] = useState('desc');

  useEffect(() => {
    if (isExecutive || isManager) return;
    const fetchManagers = async () => {
      try {
        const managersRes = await fieldSalesService.getSalesReps({ role: 'sales_manager,export_manager' });
        if (managersRes.success) {
          setManagers(managersRes.data || []);
        }
      } catch (err) {
      }
    };

    fetchManagers();
  }, [isExecutive, isManager]);

  useEffect(() => {
    if (isExecutive) return;
    const fetchExecutives = async () => {
      try {
        const params = { role: 'sales_executive,export_executive,business_relationship_manager,business_development_executive' };
        if (isManagerScope) {
          params.managerId = currentUserId;
        } else if (selectedManager !== 'all') {
          params.managerId = selectedManager;
        }
        const executivesRes = await fieldSalesService.getSalesReps(params);
        if (executivesRes.success) {
          setExecutives(executivesRes.data || []);
        }
      } catch (err) {
      }
    };

    fetchExecutives();
  }, [isExecutive, isManagerScope, currentUserId, selectedManager]);

  // KPIs, weekly trends, recent quotes — date range + team filters
  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const isCustom = Boolean(customRange.startDate && customRange.endDate);
        const finalRange = isCustom ? 'custom' : dateRange;

        const result = await quoteOrderAnalyticsService.getQuoteOrderAnalytics(
          finalRange,
          isCustom ? customRange.startDate : '',
          isCustom ? customRange.endDate : '',
          selectedManager,
          selectedExecutive
        );

        if (!isMounted) return;

        if (result.success && result.data) {
          setKpis(result.data.kpis || {
            totalQuotations: 0,
            quotesAccepted: 0,
            orders: 0,
            quoteToAcceptedRate: 0
          });
          setWeeklyTrends(result.data.weeklyTrends || []);
          setRecentQuotes(result.data.recentQuotes || []);
        } else {
          setKpis({
            totalQuotations: 0,
            quotesAccepted: 0,
            orders: 0,
            quoteToAcceptedRate: 0
          });
          setWeeklyTrends([]);
          setRecentQuotes([]);
        }
      } catch {
        if (!isMounted) return;
        setKpis({
          totalQuotations: 0,
          quotesAccepted: 0,
          quoteToAcceptedRate: 0
        });
        setWeeklyTrends([]);
        setRecentQuotes([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboardData();
    return () => {
      isMounted = false;
    };
  }, [dateRange, customRange.startDate, customRange.endDate, selectedManager, selectedExecutive]);

  // Last 6 months line chart — manager/executive only (ignores date filters)
  useEffect(() => {
    let isMounted = true;

    const fetchSixMonthTrend = async () => {
      setChartLoading(true);
      try {
        const result = await quoteOrderAnalyticsService.getQuoteOrderAnalytics(
          'all_time',
          '',
          '',
          selectedManager,
          selectedExecutive
        );

        if (!isMounted) return;

        if (result.success && result.data) {
          setSixMonthTrend(result.data.sixMonthTrend?.series || []);
          setSixMonthBreakdown(result.data.sixMonthBreakdown?.series || []);
        } else {
          setSixMonthTrend([]);
          setSixMonthBreakdown([]);
        }
      } catch {
        if (!isMounted) return;
        setSixMonthTrend([]);
        setSixMonthBreakdown([]);
      } finally {
        if (isMounted) setChartLoading(false);
      }
    };

    fetchSixMonthTrend();
    return () => {
      isMounted = false;
    };
  }, [selectedManager, selectedExecutive]);


  // Check if quote is expiring soon (within 7 days)
  const isExpiringSoon = (quote) => {
    if (!quote.expiresOn) return false;
    const expiresDate = new Date(quote.expiresOn);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiresDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  };

  // Handle drill down
  const handleDrillDown = (type, filters = {}) => {
  };

  // Handle trends sorting
  const handleTrendsSort = (column) => {
    if (trendsSortColumn === column) {
      setTrendsSortDirection(trendsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTrendsSortColumn(column);
      setTrendsSortDirection('asc');
    }
  };

  // Get sorted weekly trends
  const getSortedWeeklyTrends = () => {
    if (!trendsSortColumn || !weeklyTrends || weeklyTrends.length === 0) return weeklyTrends;

    return [...weeklyTrends].sort((a, b) => {
      let aValue, bValue;

      if (trendsSortColumn === 'label') {
        aValue = a.label || '';
        bValue = b.label || '';
      } else if (trendsSortColumn === 'total') {
        aValue = Number(a.total) || 0;
        bValue = Number(b.total) || 0;
      } else if (trendsSortColumn === 'accepted') {
        aValue = Number(a.accepted) || 0;
        bValue = Number(b.accepted) || 0;
      } else if (trendsSortColumn === 'acceptedRate') {
        aValue = Number(a.acceptedRate) || 0;
        bValue = Number(b.acceptedRate) || 0;
      } else {
        aValue = a[trendsSortColumn] || '';
        bValue = b[trendsSortColumn] || '';
      }

      const order = trendsSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Handle quotes sorting
  const handleQuotesSort = (column) => {
    if (quotesSortColumn === column) {
      setQuotesSortDirection(quotesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setQuotesSortColumn(column);
      setQuotesSortDirection('asc');
    }
  };

  // Get sorted recent quotes
  const getSortedRecentQuotes = () => {
    if (!quotesSortColumn || !recentQuotes || recentQuotes.length === 0) return recentQuotes;

    return [...recentQuotes].sort((a, b) => {
      let aValue, bValue;

      if (quotesSortColumn === 'quoteId') {
        aValue = (a.quoteId || '').toLowerCase();
        bValue = (b.quoteId || '').toLowerCase();
      } else if (quotesSortColumn === 'companyName') {
        aValue = (a.companyName || '').toLowerCase();
        bValue = (b.companyName || '').toLowerCase();
      } else if (quotesSortColumn === 'value') {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (quotesSortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (quotesSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (quotesSortColumn === 'expiresOn') {
        aValue = new Date(a.expiresOn || 0).getTime();
        bValue = new Date(b.expiresOn || 0).getTime();
      } else {
        aValue = a[quotesSortColumn] || '';
        bValue = b[quotesSortColumn] || '';
      }

      const order = quotesSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Handle quote row click - navigate to send-track page
  const handleQuoteClick = (quoteId) => {
    navigate(`/crm/quotation-proposal/send-track/${quoteId}`);
  };

  // Loading skeleton component
  const KPISkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
      <div className="h-8 bg-gray-200 rounded w-16"></div>
    </div>
  );

  const TableRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
    </tr>
  );

  return (
    <div className="space-y-6">
      {/* Header with Filters and Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Quote → Accepted</h2>
            <p className="text-sm text-gray-600">Proposal traction and acceptance analysis</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {!isExecutive && (
            isManager ? (
              <select
                value={selectedManager}
                onChange={(e) => {
                  const nextManager = e.target.value;
                  setSelectedManager(nextManager);
                  setSelectedExecutive(nextManager === 'all' ? 'all' : 'manager_only');
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                disabled={loading || isManager}
              >
                <option value="all">All Data</option>
                <option value={currentUserId}>{currentUserName}</option>
              </select>
            ) : (
              <select
                value={selectedManager}
                onChange={(e) => {
                  setSelectedManager(e.target.value);
                  setSelectedExecutive('all');
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                disabled={loading}
              >
                <option value="all">All Managers</option>
                {managers.map((manager) => (
                  <option key={manager._id} value={manager._id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            )
          )}

          {!isExecutive && (
            <select
              value={selectedExecutive}
              onChange={(e) => {
                const nextExecutive = e.target.value;
                if (isManager) {
                  setSelectedManager(currentUserId);
                }
                setSelectedExecutive(nextExecutive);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={loading}
            >
              <option value="all">All</option>
              {(isManager || selectedManager !== 'all') && <option value="my_team">My Team</option>}
              {(isManager || selectedManager !== 'all') && <option value="manager_only">My Data</option>}
              {executives.map((executive) => (
                <option key={executive._id} value={executive._id}>
                  {executive.name}
                </option>
              ))}
            </select>
          )}

          {(!customRange.startDate && !customRange.endDate) && (
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={loading}
            >
              <option value="all_time">All Time</option>
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_60_days">Last 60 Days</option>
              <option value="last_90_days">Last 90 Days</option>
            </select>
          )}

          <DateRangeFilter
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
            allowFutureDates={false}
          />

          {(customRange.startDate || customRange.endDate) && (
            <Button
              variant="outline"
              disabled={loading}
              onClick={() => {
                setCustomRange({ startDate: '', endDate: '' });
                setDateRange('last_30_days');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards - 3 KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading ? (
          <>
            <KPISkeleton />
            <KPISkeleton />
            <KPISkeleton />
          </>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleDrillDown('total-quotes')}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Quotes</span>
                <FileText className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">{kpis.totalQuotations}</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleDrillDown('quotes-accepted')}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Accepted</span>
                <CheckCircle className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-green-600">{kpis.quotesAccepted}</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Quote→Accepted %</span>
                <Percent className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-purple-600">{kpis.quoteToAcceptedRate}%</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Last 6 months — quotes created vs accepted */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Last 6 Months Trend</h3>
        <p className="text-xs text-gray-500 mb-4">
          Created by quote created date; accepted by accepted date. Uses sales manager and executive filters only (not affected by the date filters above).
        </p>
        {chartLoading ? (
          <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm animate-pulse">
            Loading chart...
          </div>
        ) : sixMonthTrend.length ? (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sixMonthTrend} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="month"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickLine={{ stroke: '#cbd5e1' }}
                  axisLine={{ stroke: '#94a3b8' }}
                />
                <YAxis
                  stroke="#64748b"
                  fontSize={12}
                  allowDecimals={false}
                  tickLine={{ stroke: '#cbd5e1' }}
                  axisLine={{ stroke: '#94a3b8' }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)'
                  }}
                  formatter={(value, name) => [
                    new Intl.NumberFormat('en-IN').format(value),
                    name
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
                <Line
                  type="monotone"
                  dataKey="created"
                  name="Quotes Created"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#3b82f6' }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="accepted"
                  name="Quotes Accepted"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#10b981' }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">
            No quote data for the selected filters
          </div>
        )}
      </div>

      {/* Last 6 months — breakdown by stage */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Last 6 Months — Quote Pipeline</h3>
        <p className="text-xs text-gray-500 mb-4">
          Created, sent, accepted, and rejected by month. Hover a bar for quantity and amount. Filtered by sales manager and executive only.
        </p>
        {chartLoading ? (
          <div className="flex items-center justify-center h-[320px] text-gray-400 text-sm animate-pulse">
            Loading breakdown...
          </div>
        ) : sixMonthBreakdown.length ? (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sixMonthBreakdown} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={{ stroke: '#cbd5e1' }} axisLine={{ stroke: '#94a3b8' }} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} tickLine={{ stroke: '#cbd5e1' }} axisLine={{ stroke: '#94a3b8' }} />
                <Tooltip content={<BreakdownTooltip />} shared cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }} />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                {BREAKDOWN_METRICS.map((metric) => (
                  <Bar
                    key={metric.countKey}
                    dataKey={metric.countKey}
                    name={metric.label}
                    fill={BREAKDOWN_COLORS[metric.key]}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">
            No breakdown data for the selected team filters
          </div>
        )}
      </div>

      {/* Trend Analysis */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Weekly Trends</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { key: 'label', label: 'Period' },
                  { key: 'ordered', label: 'Placed Order' },
                  { key: 'accepted', label: 'Quotation Accepted' },
                  { key: 'rejected', label: 'Quotation Rejected' },
                  { key: 'seen', label: 'Quotation Seen' },
                  { key: 'sent', label: 'Quotation Sent' },
                  { key: 'created', label: 'Quote Created' },
                  { key: 'draft', label: 'Quote Draft' },
                  { key: 'order', label: 'Ready to Order' },
                ].map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleTrendsSort(col.key)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{col.label}</span>
                      {trendsSortColumn === col.key ? (
                        trendsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <TableRowSkeleton key={index} />
                ))
              ) : weeklyTrends.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-sm text-gray-500">
                    No trend data available for the selected period
                  </td>
                </tr>
              ) : (
                getSortedWeeklyTrends().map((week, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {week.label}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.ordered ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.accepted ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.rejected ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.seen ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.sent ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.created ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.draft ?? 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {week.order ?? 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Quotes Table - Same structure as CreateQuotesList */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent</h3>
            <button
              onClick={() => navigate('/crm/quotation-proposal/create-quotes')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              type="button"
            >
              View All
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleQuotesSort('quoteId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Quote ID</span>
                      {quotesSortColumn === 'quoteId' ? (
                        quotesSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleQuotesSort('companyName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Company</span>
                      {quotesSortColumn === 'companyName' ? (
                        quotesSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleQuotesSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {quotesSortColumn === 'value' ? (
                        quotesSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleQuotesSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {quotesSortColumn === 'status' ? (
                        quotesSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleQuotesSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {quotesSortColumn === 'createdAt' ? (
                        quotesSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleQuotesSort('expiresOn')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Expiry</span>
                      {quotesSortColumn === 'expiresOn' ? (
                        quotesSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <>
                    {[...Array(5)].map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))}
                  </>
                ) : recentQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                      No recent quotes found
                    </td>
                  </tr>
                ) : (
                  getSortedRecentQuotes().map((quote) => {
                    const createdDate = quote.createdAt;
                    const formattedDate = createdDate ? formatDate(createdDate, { includeTime: true }) : 'N/A';
                    const expiredDate = quote.expiresOn ? formatDate(quote.expiresOn, { includeTime: false }) : 'N/A';
                    const statusText = formatQuotationStatus(quote.status);
                    const orderInfo = quote.isConvertedToOrder && quote.orderId ? `(Order: ${quote.orderId})` : '';
                    const fullStatusText = `${statusText} ${orderInfo}`.trim();

                    return (
                      <tr
                        key={quote.id}
                        className={`hover:bg-gray-50 ${isExpiringSoon(quote) ? 'bg-red-100 border-l-4 border-red-500' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={quote.quoteId || 'N/A'}>
                            {quote.quoteId || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 capitalize truncate max-w-xs" title={quote.companyName || 'N/A'}>
                            {quote.companyName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{formatIndianAmount(quote.value || 0)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span
                              className={`inline-flex items-center mr-auto px-2.5 py-0.5 rounded-full text-xs font-medium truncate max-w-xs ${['order', 'ordered'].includes(quote.status?.toLowerCase())
                                ? getQuotationStatusClasses('accepted')
                                : getQuotationStatusClasses(quote.status)}`}
                              title={fullStatusText}
                            >
                              {['order', 'ordered'].includes(quote.status?.toLowerCase()) ? 'Accepted' : formatQuotationStatus(quote.status)}
                            </span>
                            {quote.status?.toLowerCase() === 'order' && (
                              <span className="text-[11px] text-gray-500 mt-1 ml-1 italic font-medium">(Ready to order)</span>
                            )}
                            {quote.status?.toLowerCase() === 'ordered' && (
                              <span className="text-[11px] text-gray-500 mt-1 ml-1 italic font-medium">
                                (Ordered)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formattedDate}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">
                            {expiredDate}
                            {isExpiringSoon(quote) && (
                              <div className="text-xs text-yellow-600 mt-1">
                                Expires in {Math.ceil((new Date(quote.expiresOn) - new Date()) / (1000 * 60 * 60 * 24))} days
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteOrderDashboard;
